/**
 * Host Team Progress Modal
 *
 * Displays a popup showing the progress of every team across one or more games.
 * For combined (multi-template) events it merges all teams from every game into a
 * single leaderboard sorted by score, regardless of which game a team came from.
 * Each team's progress is still computed against its own game's question count.
 * Columns: Team, Progress, (Time left), Score.
 */

import React from 'react'
import Modal from 'react-bootstrap/Modal'
import TeamTimeRemaining from './team-time-remaining'

function countCompleted(answers) {
  let completed = 0
  if (answers) {
    for (const key in answers) {
      const val = answers[key]
      if (val.isCorrect) {
        completed += 1
      } else if (val.numGuesses >= 2) {
        completed += 1
      }
    }
  }
  return completed
}

const TeamProgressModal = ({ modalShow, modalClose, hunts }) => {
  const huntList = (hunts || []).filter(Boolean)
  const enableTimer = huntList.some(h => h.enable_timer)

  // Merge every team from every game into a single leaderboard sorted by score,
  // regardless of which game the team came from. Permanent teams are excluded.
  // Each team stays paired with its own hunt so progress/timer resolve per game.
  const rows = []
  huntList.forEach(hunt => {
    const totalQuestions =
      (hunt.questions ? hunt.questions.length : 0) +
      (hunt.secondary_questions ? hunt.secondary_questions.length : 0)
    ;(hunt.teams || [])
      .filter(team => !team.permanent)
      .forEach(team => rows.push({ hunt, team, totalQuestions }))
  })
  rows.sort((a, b) => {
    if ((a.team.total_score || 0) > (b.team.total_score || 0)) return -1
    if ((a.team.total_score || 0) < (b.team.total_score || 0)) return 1
    // tiebreaker (e.g. before the game starts): team number ascending
    return (a.team.number || 0) - (b.team.number || 0)
  })

  return (
    <Modal show={modalShow} onHide={modalClose} centered scrollable={true} aria-labelledby="progress-modal-title">
      <Modal.Header className={`card-header`} closeButton>
        <Modal.Title id="progress-modal-title" className={`card-title mb-2`}>
          Team Progress
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={`card-body bg-white text-dark`}>
        {huntList.length === 0 ? (
          <p className="mb-0">Loading hunt data...</p>
        ) : (
          <>
            <div className="row mb-3">
              <div className="col">
                <strong>Team</strong>
              </div>
              <div className="col">
                <strong>Progress</strong>
              </div>
              {enableTimer && (
                <div className="col">
                  <strong>Time left</strong>
                </div>
              )}
              <div className="col">
                <strong>Score</strong>
              </div>
            </div>
            {rows.map(({ hunt, team, totalQuestions }) => {
              const team_completed =
                countCompleted(team.answers) + countCompleted(team.secondary_answers)
              const team_finished = team.end_time ? `text-success` : ``
              return (
                <div key={team.id} className="pb-2 mb-2" style={{ borderBottom: `1px solid #ccc` }}>
                  <div className="row">
                    <div className={`col team-number`}>
                      <span className={`title h6`}>
                        {team.number} - {team.access_code}
                      </span>
                      <span className={`d-block team-details`}>{team.team_name ? team.team_name : ``}</span>
                    </div>
                    <div className={`col ${team_finished}`}>
                      {team_completed} / {totalQuestions}
                    </div>
                    {enableTimer && (
                      <div className={`col`}>
                        {hunt.enable_timer ? (
                          <TeamTimeRemaining
                            team={team}
                            duration={hunt.duration}
                            enableTimer={hunt.enable_timer}
                            inline
                          />
                        ) : (
                          `—`
                        )}
                      </div>
                    )}
                    <div className={`col`}>{team.total_score}</div>
                  </div>
                </div>
              )
            })}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className={`btn btn-block btn-tiny mt-4`} onClick={modalClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

TeamProgressModal.defaultProps = {
  modalShow: false,
  modalDismissed: false,
  modalClose: () => {},
  hunts: [],
}

export default TeamProgressModal
